import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ActionLogTableRow } from "../../admin/actionLog/ActionLogTableRow";
import { ActionLogInfo } from "@services/admin/types";
import { apiClient } from "../../utils/apiClient";
import { useActionLogHandlers } from "../../admin/hooks/useActionLogHandlers";

interface ActionLogsPanelProps {
  emailId: string;
}

/**
 * Panel to display action logs for an email (admin only)
 */
export const ActionLogsPanel: React.FC<ActionLogsPanelProps> = ({ emailId }) => {
  const [logs, setLogs] = useState<ActionLogInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Use the reusable action log handlers hook
  const {
    handleViewEmail,
    handleViewDetails,
    handleViewGptRequest,
    handleViewExtractedData,
    handleViewDerivedData,
    handleViewShipmentEmail,
    handleViewShipmentFeed,
    handleViewAIAgentTrace,
    handleViewErrors,
    handleViewReminder,
  } = useActionLogHandlers();

  // Fetch logs when accordion is expanded
  const handleAccordionChange = async (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);

    if (isExpanded && logs.length === 0) {
      setLoading(true);
      try {
        const response = await apiClient.post<{ logs: ActionLogInfo[]; total: number }>(
          "/admin/action-logs/list",
          { emailId, limit: 100 },
          { withCredentials: true }
        );
        setLogs(response.logs);
      } catch (error: any) {
        console.error("Failed to fetch action logs:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  // Refresh logs when emailId changes
  useEffect(() => {
    if (expanded) {
      setLoading(true);
      apiClient
        .post<{ logs: ActionLogInfo[]; total: number }>(
          "/admin/action-logs/list",
          { emailId, limit: 100 },
          { withCredentials: true }
        )
        .then((response: { logs: ActionLogInfo[]; total: number }) => {
          setLogs(response.logs);
        })
        .catch((error: any) => {
          console.error("Failed to fetch action logs:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [emailId, expanded]);

  return (
    <Box sx={{ mb: 2 }}>
      <Accordion expanded={expanded} onChange={handleAccordionChange}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2" fontWeight="bold">
            Action Logs {logs.length > 0 && `(${logs.length})`}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : logs.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No action logs found for this email.
              </Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Memo</TableCell>
                  <TableCell align="center">Errors</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <ActionLogTableRow
                    key={log.id}
                    log={log}
                    includeDescription={false}
                    displayErrors={true}
                    onViewDetails={handleViewDetails}
                    onViewGptRequest={handleViewGptRequest}
                    onViewExtractedData={handleViewExtractedData}
                    onViewDerivedData={handleViewDerivedData}
                    onViewShipmentEmail={handleViewShipmentEmail}
                    onViewShipmentFeed={handleViewShipmentFeed}
                    onViewAIAgentTrace={handleViewAIAgentTrace}
                    onViewErrors={handleViewErrors}
                    onViewEmail={handleViewEmail}
                    onViewReminder={handleViewReminder}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
